import React, { useEffect, useState } from "react";
import axios from "axios";
import { value } from "../../config/data";

const WithdrawalInvoice = ({
    invoiceForType = "bd", // "bd" or "agency"
    invoiceFor: propInvoiceFor = "",
    startDate: propStartDate,
    endDate: propEndDate,
    onBack,
    toShowSubmit = false,
}) => {
    const [data, setData] = useState([]);
    const [invoiceFor, setInvoiceFor] = useState(propInvoiceFor || "");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [startDate, setStartDate] = useState(propStartDate || "");
    const [endDate, setEndDate] = useState(propEndDate || "");
    const [submitMsg, setSubmitMsg] = useState("");
    const [billPath, setBillPath] = useState("");
    const [billApproved, setBillApproved] = useState(0);

    // Pick uniqueId from sessionStorage based on invoiceForType if not passed as prop
    useEffect(() => {
        if (!propInvoiceFor) {
            let sessionKey = invoiceForType;
            const storedData = sessionStorage.getItem(sessionKey);
            if (storedData) {
                try {
                    const obj = JSON.parse(storedData);
                    setInvoiceFor(obj.uniqueId || "");
                } catch {
                    setInvoiceFor("");
                }
            }
        } else {
            setInvoiceFor(propInvoiceFor);
        }
        if (!propStartDate) setStartDate("");
        if (!propEndDate) setEndDate("");
        // eslint-disable-next-line
    }, [propInvoiceFor, invoiceForType, propStartDate, propEndDate]);

    useEffect(() => {
        const fetchData = async () => {
            if (!invoiceFor) return;
            setLoading(true);
            setError("");
            try {
                const params = { invoiceFor, invoiceForType };
                if (propStartDate) params.start = propStartDate;
                if (propEndDate) params.end = propEndDate;
                const res = await axios.get(
                    `${process.env.REACT_APP_BASEURL1}/admin/withdrawal/InvoiceForBd`,
                    { params }
                );
                if (res.data.success) {
                    setData(res.data.details || []);
                    setStartDate(res.data.startDate ? new Date(res.data.startDate) : "");
                    setEndDate(res.data.endDate ? new Date(res.data.endDate) : "");
                    setBillPath(res.data.billPath || "");
                    setBillApproved(res.data.billApproved || 0);
                } else {
                    setError(res.data.message || "Failed to fetch data");
                }
            } catch (err) {
                setError(err.message || "Error fetching data");
            }
            setLoading(false);
        };
        fetchData();
        // eslint-disable-next-line
    }, [invoiceFor, invoiceForType, propStartDate, propEndDate, submitMsg]);

    const formatDate = (date) =>
        date
            ? new Date(date).toLocaleDateString("en-GB", {
                year: "numeric",
                month: "short",
                day: "2-digit",
            })
            : "-";

    // Calculate totals
    const hostAmount = data.reduce(
        (sum, item) =>
            sum +
            parseFloat(
                ((item?.totalDollor || 0) * value.USDINR).toFixed(2)
            ),
        0
    );
    const agComm = data.reduce(
        (sum, item) =>
            sum +
            parseFloat(
                (
                    ((item?.totalDollor || 0) * value.USDINR) *
                    value.agencyComm
                ).toFixed(2)
            ),
        0
    );
    const total = hostAmount + agComm;

    const handleDownload = () => {
        if (billApproved === 1 && billPath) {
            const link = document.createElement("a");
            link.href = `${process.env.REACT_APP_BASEURL1}/${billPath}`;
            link.target = "_blank";
            link.download = "Paid_Bill.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            alert("Bill is not approved yet or path is not available.");
        }
    };

    const handleSubmit = async () => {
        setSubmitMsg("");
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BASEURL1}/admin/withdrawal/submitBdBill`,
                {
                    invoiceFor,
                    invoiceForType,
                    totalAmount: total,
                    startDate,
                    endDate
                }
            );
            if (res.data.success) {
                setSubmitMsg("Bill submitted successfully! Awaiting admin approval.");
            } else {
                setSubmitMsg(res.data.message || "Failed to submit bill.");
            }
        } catch (e) {
            setSubmitMsg("Failed to submit bill.");
        }
    };

    return (
        <div
            style={{
                maxWidth: 900,
                margin: "40px auto",
                padding: 32,
                background: "#fff",
                border: "2px solid #222",
                borderRadius: 12,
                fontFamily: "serif",
                boxShadow: "0 4px 24px #0002",
                position: "relative",
            }}
        >
            <div style={{ textAlign: "center", marginBottom: 8 }}>
                <img
                    src="/mastiLive_logo.png"
                    alt="Company Logo"
                    style={{ height: 60, marginBottom: 8 }}
                />
                <h1 style={{ margin: 0, fontWeight: 700, letterSpacing: 2 }}>
                    MastiShorts Private Limited
                </h1>
                <div style={{ fontSize: 18, fontWeight: 600, margin: "8px 0 0 0" }}>
                    Computer Generated Bill
                </div>
                <div style={{ fontSize: 14, color: "#555" }}>
                    <b>Period:</b> {formatDate(startDate)} to {formatDate(endDate)}
                </div>
            </div>
            <hr style={{ margin: "18px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                <div>
                    <b>{invoiceForType === "bd" ? "BD Unique ID:" : "Agency Unique ID:"}</b> {invoiceFor}
                </div>
                <div>
                    <b>Date:</b> {formatDate(new Date())}
                </div>
            </div>
            <div
                style={{
                    background: "#f7f7f7",
                    border: "1px solid #ddd",
                    padding: 16,
                    borderRadius: 8,
                    marginBottom: 18,
                    fontSize: 20,
                    fontWeight: 700,
                    textAlign: "center",
                    letterSpacing: 1,
                }}
            >
                Total Amount (INR): ₹{total.toLocaleString("en-IN")}
            </div>
            <div style={{ marginBottom: 18, display: "flex", gap: 32, fontSize: 16 }}>
                <div>
                    <b>Host Amount:</b> ₹{hostAmount.toLocaleString("en-IN")}
                </div>
                <div>
                    <b>Agency Commission:</b> ₹{agComm.toLocaleString("en-IN")}
                </div>
                <div>
                    <b>Total:</b> ₹{total.toLocaleString("en-IN")}
                </div>
            </div>
            {loading && <p>Loading...</p>}
            {error && <p style={{ color: "red" }}>{error}</p>}
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 24, fontSize: 15 }}>
                <thead>
                    <tr style={{ background: "#f5f5f5" }}>
                        <th style={{ border: "1px solid #ccc", padding: 8 }}>#</th>
                        {invoiceForType === "bd" ? (
                            <>
                                <th style={{ border: "1px solid #ccc", padding: 8 }}>Agency</th>
                                <th style={{ border: "1px solid #ccc", padding: 8 }}>Id</th>
                            </>
                        ) : (
                            <>
                                <th style={{ border: "1px solid #ccc", padding: 8 }}>Username</th>
                                <th style={{ border: "1px solid #ccc", padding: 8 }}>UserId</th>
                            </>
                        )}
                        <th style={{ border: "1px solid #ccc", padding: 8 }}>Coins</th>
                        <th style={{ border: "1px solid #ccc", padding: 8 }}>Amount(USD)</th>
                        <th style={{ border: "1px solid #ccc", padding: 8 }}>Amount(INR)</th>
                        <th style={{ border: "1px solid #ccc", padding: 8 }}>Ag Comm(10%)</th>
                        <th style={{ border: "1px solid #ccc", padding: 8 }}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 && !loading ? (
                        <tr>
                            <td colSpan={8} style={{ textAlign: "center", padding: 16 }}>
                                No records found
                            </td>
                        </tr>
                    ) : (
                        data.map((item, idx) => {
                            const inr = parseFloat(
                                ((item?.totalDollor || 0) * value.USDINR).toFixed(2)
                            );
                            const agc = parseFloat((inr * value.agencyComm).toFixed(2));
                            return (
                                <tr key={item._id || idx}>
                                    <td style={{ border: "1px solid #ccc", padding: 8 }}>{idx + 1}</td>
                                    {invoiceForType === "bd" ? (
                                        <>
                                            <td style={{ border: "1px solid #ccc", padding: 8 }}>{item.agencyName || "-"}</td>
                                            <td style={{ border: "1px solid #ccc", padding: 8 }}>{item.agencyId || "-"}</td>
                                        </>
                                    ) : (
                                        <>
                                            <td style={{ border: "1px solid #ccc", padding: 8 }}>{item.username || "-"}</td>
                                            <td style={{ border: "1px solid #ccc", padding: 8 }}>{item.userId || "-"}</td>
                                        </>
                                    )}
                                    <td style={{ border: "1px solid #ccc", padding: 8 }}>{item.totalAmount || "-"}</td>
                                    <td style={{ border: "1px solid #ccc", padding: 8 }}>{item.totalDollor?.toFixed(2) || "-"}</td>
                                    <td style={{ border: "1px solid #ccc", padding: 8 }}>
                                        <span style={{ fontWeight: "bold" }}>Rs </span>
                                        {inr.toLocaleString("en-IN")}
                                    </td>
                                    <td style={{ border: "1px solid #ccc", padding: 8 }}>
                                        <span style={{ fontWeight: "bold" }}>Rs </span>
                                        {agc.toLocaleString("en-IN")}
                                    </td>
                                    <td style={{ border: "1px solid #ccc", padding: 8 }}>
                                        <span style={{ color: "green", fontWeight: 600 }}>Approved</span>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
            {toShowSubmit && billApproved !== 1 && <div
                style={{
                    background: "#f9f9f9",
                    border: "1px dashed #888",
                    padding: 16,
                    borderRadius: 8,
                    marginBottom: 18,
                    color: "#444",
                    fontSize: 16,
                    textAlign: "center",
                }}
            >
                <b>This is a computer generated bill.</b> <br />
                <span style={{ color: "#c72a16" }}>This is unpaid bill.</span><br />
                You need to submit it.<br />
                Once submitted and after approved, you will get downloadable paid bill.
            </div>}
            {submitMsg && (
                <div style={{ color: "#06690d", textAlign: "center", marginBottom: 12 }}>
                    {submitMsg}
                </div>
            )}
            <div style={{ textAlign: "center", marginTop: 24 }}>
                {toShowSubmit && billApproved !== 1 && (
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        style={{
                            background: "#c72a16",
                            color: "#fff",
                            padding: "10px 32px",
                            fontSize: 18,
                            border: "none",
                            borderRadius: 6,
                            cursor: loading ? "not-allowed" : "pointer",
                            fontWeight: 600,
                            letterSpacing: 1,
                            marginRight: 16,
                            boxShadow: "0 2px 8px #c72a1644",
                            transition: "all 0.2s",
                        }}
                    >
                        Submit Bill
                    </button>
                )}
                {toShowSubmit && billApproved === 1 && billPath && (
                    <button
                        onClick={handleDownload}
                        style={{
                            background: "#06690d",
                            color: "#fff",
                            padding: "10px 32px",
                            fontSize: 18,
                            border: "none",
                            borderRadius: 6,
                            fontWeight: 600,
                            letterSpacing: 1,
                            boxShadow: "0 2px 8px #06690d44",
                            transition: "all 0.2s",
                            cursor: "pointer"
                        }}
                    >
                        Download Paid Bill
                    </button>
                )}
            </div>
        </div>
    );
};

export default WithdrawalInvoice;